'use client';

import classNames from 'classnames';
import { MenuIcon, XIcon } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { FC, useEffect, useState } from 'react';

import { useStore } from '@/entities/store';
import { View } from '@/shared/ui/quarks/view/ui';

import { mainLinks } from './lib';

const LogoImage: FC<{
  className?: string;
}> = () => <Link href="/">
  <Image
    className={classNames('mx-auto absolute top-0 left-1/2 -translate-x-1/2')}
    width="120"
    height="94"
    src="/header-logo.png"
    alt="Metro: After the end"
  />
</Link>

const MenuLink: FC<{
  href: string;
  title: string;
  className?: string;
  onClick?: () => void;
}> = ({ href, title, className, onClick }) => {
  const currentPath = usePathname();
  const isActive = currentPath === href && !onClick;

  return <Link href={href} className={classNames('hover:text-red-700 max-md:uppercase max-md:bg-red-700/20 max-md:w-full max-md:p-4 max-md:rounded-sm', {
    'text-red-700': isActive,
  }, className)}
    onClick={onClick}>
    {title}
  </Link>
}

const MainLinks = observer(({ className }: { className?: string }) =>
  <nav className={classNames('flex justify-between items-center gap-[35px] text-white', className)}>
    {mainLinks.map((link) => (
      <MenuLink
        key={link.href}
        href={link.href}
        title={link.title}
      />
    ))}
  </nav>)

const AuthLinks = observer(({ isAuthorized, logout, className }: { isAuthorized: boolean, logout: () => void, className?: string }) =>
  <nav className={classNames('flex justify-between items-center gap-[35px] text-white ml-auto', className)}>
    {!isAuthorized && (
      <>
        <MenuLink href="/auth/sign-in" title="Войти" />
        <MenuLink href="/auth/sign-up" title="Регистрация" />
      </>
    )}
    {isAuthorized && (
      <>
        <MenuLink href="/profile" title="Профиль" />
        <MenuLink href="/" title="Выход" onClick={logout} />
      </>
    )}
  </nav>)

const Header = observer(() => {
  const currentPath = usePathname();
  const {
    user: { isAuthorized, logout },
  } = useStore();

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [isMenuOpen])

  return (
    <header className="sticky top-0 backdrop-blur-md bg-black/70 px-[40px] py-[0] h-[98px] flex-shrink-0 z-10">
      <div className="relative flex justify-between items-center">
        {!isMenuOpen && <MenuIcon className="h-6 w-6 hidden max-md:block z-10 text-white" onClick={() => setIsMenuOpen(!isMenuOpen)} />}
        {isMenuOpen && <XIcon className="h-6 w-6 hidden max-md:block z-10 text-white" onClick={() => setIsMenuOpen(!isMenuOpen)} />}

        <div className={classNames('w-full flex h-[98px] justify-center items-center gap-[40px]')}>
          <MainLinks currentPath={currentPath} className='max-md:hidden' />

          <LogoImage className='max-md:hidden' />

          <AuthLinks className='max-md:hidden' isAuthorized={isAuthorized} logout={logout} />
        </div>
      </div>

      <View.Condition if={isMenuOpen}>
        <div className='absolute top-0 left-0 w-full h-screen bg-black'>
          <div className='flex flex-col gap-2 items-center mt-20 h-full'>
            <LogoImage />

            <MainLinks className='w-full flex flex-col !gap-10 mb-10' />

            <AuthLinks className='w-full flex flex-col !gap-10 mr-auto' isAuthorized={isAuthorized} logout={logout} />
          </div>
        </div>

      </View.Condition>
    </header >
  );
});

export { Header };
